import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Logo from "./Logo";
import { useAuthStore } from "../store/authStore";
import { useOnlineWebSocket } from "../hooks/useOnlineWebSocket";

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #081c21;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px 48px 24px 32px;
  overflow-y: auto;
  gap: 16px;
  max-width: calc(100vw - 240px);

  @media (max-width: 768px) {
    padding: 16px;
    max-width: 100%;
  }
`;

const MobileHeader = styled.div`
  display: none;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: rgba(8, 28, 33, 0.8);
  border-radius: 24px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VerticalDivider = styled.div`
  width: 1px;
  background-color: rgb(79 79 79 / 20%);
  min-height: 100vh;

  @media (max-width: 768px) {
    display: none;
  }
`;

interface MainLayoutProps {
  children: React.ReactNode;
  activePage?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, activePage }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Подключение к WebSocket для отслеживания онлайна
  useOnlineWebSocket();

  // Проверка авторизации и перенаправление на страницу входа, если пользователь не авторизован
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <LayoutContainer>
      {/* Сайдбар отображается по-разному на мобильных и десктопных устройствах */}
      <Sidebar
        activePage={activePage}
        isVisible={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      {/* Вертикальный разделитель между sidebar и основным контентом */}
      <VerticalDivider />

      <ContentContainer>
        {/* Мобильная шапка видна только на мобильных устройствах */}
        <MobileHeader>
          <Logo variant="default" size="small" vertical={true} />
          <MenuButton onClick={toggleMobileMenu}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 7H21"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M3 12H21"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M3 17H21"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </MenuButton>
        </MobileHeader>

        {children}
      </ContentContainer>
    </LayoutContainer>
  );
};

export default MainLayout;
